import { useState } from 'react';
import toast from 'react-hot-toast';
import { UserSearch, UserPlus, UserCheck, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { clientesAPI } from '../services/api';
import { invalidateClientes } from '../services/cachedCatalogs';
import { Modal, Button, Input, Tooltip, PhoneInput } from './ui';
import ClienteTipoToggle, { empresaEfectiva, TIPO_EMPRESA, TIPO_PERSONA } from './ClienteTipoToggle';

/**
 * Acciones rápidas sobre el cliente de una cotización: crear uno nuevo con su
 * contacto (wizard de 3 pasos) y cargar los datos de un contacto existente.
 *
 * Se usa en CotizacionForm y en CotizacionFormManual — la lógica vive acá para
 * que ambos flujos se comporten igual.
 *
 * Props:
 *   - clienteSeleccionado: opción de react-select { value, label, data }
 *   - onClienteCreado(cliente): el padre lo selecciona y rellena sus campos
 *   - onContactoSeleccionado(contacto): rellena nombre/email/teléfono
 *       (la empresa NO cambia: sigue siendo la del cliente principal)
 *   - onClientesActualizados(lista): lista refrescada tras crear
 */
const ClienteAcciones = ({
    clienteSeleccionado,
    onClienteCreado,
    onContactoSeleccionado,
    onClientesActualizados
}) => {
    // Selector de contactos del cliente
    const [showContactosModal, setShowContactosModal] = useState(false);
    const [contactosCliente, setContactosCliente] = useState([]);
    const [loadingContactos, setLoadingContactos] = useState(false);
    // Crear contacto desde el selector (cuando el cliente no tiene ninguno)
    const [showCrearContactoModal, setShowCrearContactoModal] = useState(false);
    const [nuevoContacto, setNuevoContacto] = useState({ nombre: '', cargo: '', email: '', telefono: '' });
    const [savingNuevoContacto, setSavingNuevoContacto] = useState(false);
    // Wizard de cliente nuevo: cliente -> contacto opcional -> confirmar
    const [wizardOpen, setWizardOpen] = useState(false);
    const [wizardStep, setWizardStep] = useState(1);
    const [wizardCliente, setWizardCliente] = useState({ nombre: '', tipo: TIPO_EMPRESA, empresa: '', email: '', telefono: '' });
    const [wizardContacto, setWizardContacto] = useState({ nombre: '', cargo: '', email: '', telefono: '' });
    const [wizardSaving, setWizardSaving] = useState(false);

    const abrirSelectorContactos = async () => {
        if (!clienteSeleccionado?.data?.id) return;
        setShowContactosModal(true);
        setLoadingContactos(true);
        try {
            const res = await clientesAPI.getContactos(clienteSeleccionado.data.id, { limit: 100 });
            setContactosCliente(res.data || []);
        } catch (err) {
            toast.error('Error al cargar contactos');
            setContactosCliente([]);
        } finally {
            setLoadingContactos(false);
        }
    };

    // Reemplaza los campos del form con los datos del contacto.
    // Empresa NO cambia (sigue siendo la empresa del cliente principal).
    const seleccionarContacto = (c) => {
        if (onContactoSeleccionado) onContactoSeleccionado(c);
        setShowContactosModal(false);
        toast.success('Contacto "' + (c.nombre || '') + '" cargado en el formulario');
    };

    // Abrir modal de crear contacto desde el selector (cuando no hay contactos).
    const abrirCrearContacto = () => {
        setNuevoContacto({ nombre: '', cargo: '', email: '', telefono: '' });
        setShowCrearContactoModal(true);
    };

    // Guardar nuevo contacto y autoseleccionarlo (rellena el form de cotizacion).
    const guardarNuevoContacto = async () => {
        if (!clienteSeleccionado?.data?.id) return;
        if (!nuevoContacto.nombre.trim()) {
            toast.error('El nombre del contacto es obligatorio');
            return;
        }
        setSavingNuevoContacto(true);
        try {
            const res = await clientesAPI.createContacto(clienteSeleccionado.data.id, {
                nombre: nuevoContacto.nombre.trim(),
                cargo: nuevoContacto.cargo.trim() || null,
                email: nuevoContacto.email.trim() || null,
                telefono: nuevoContacto.telefono.trim() || null
            });
            const creado = res.data;
            setContactosCliente(prev => [...prev, creado]);
            // Auto-seleccionarlo: rellena el form y cierra ambos modales
            seleccionarContacto(creado);
            setShowCrearContactoModal(false);
        } catch (err) {
            toast.error('Error al crear contacto');
        } finally {
            setSavingNuevoContacto(false);
        }
    };

    // ============ Wizard: Nuevo cliente ============
    const setWizardClienteCampo = (campo, valor) => {
        setWizardCliente(prev => ({ ...prev, [campo]: valor }));
    };

    const abrirWizardCliente = () => {
        setWizardCliente({ nombre: '', tipo: TIPO_EMPRESA, empresa: '', email: '', telefono: '' });
        setWizardContacto({ nombre: '', cargo: '', email: '', telefono: '' });
        setWizardStep(1);
        setWizardOpen(true);
    };

    const cerrarWizard = () => {
        if (wizardSaving) return;
        setWizardOpen(false);
    };

    const wizardContactoTieneDatos = () =>
        Boolean(wizardContacto.nombre.trim() || wizardContacto.email.trim() || wizardContacto.telefono.trim() || wizardContacto.cargo.trim());

    const avanzarWizard = () => {
        if (wizardStep === 1) {
            if (!wizardCliente.nombre.trim()) {
                toast.error('El nombre del cliente es obligatorio');
                return;
            }
            if (wizardCliente.tipo === TIPO_EMPRESA && !wizardCliente.empresa.trim()) {
                toast.error('La empresa es obligatoria');
                return;
            }
            setWizardStep(2);
        } else if (wizardStep === 2) {
            // Si llenaron algun campo del contacto, el nombre es obligatorio
            if (wizardContactoTieneDatos() && !wizardContacto.nombre.trim()) {
                toast.error('El nombre del contacto es obligatorio (o deja todos los campos vacíos para omitir)');
                return;
            }
            setWizardStep(3);
        }
    };

    const retrocederWizard = () => {
        if (wizardStep > 1) setWizardStep(wizardStep - 1);
    };

    // Crear cliente + (opcional) contacto, refrescar lista, autoseleccionar.
    const confirmarCrearCliente = async () => {
        setWizardSaving(true);
        try {
            const resCliente = await clientesAPI.create({
                nombre: wizardCliente.nombre.trim(),
                tipo: wizardCliente.tipo,
                empresa: empresaEfectiva(wizardCliente).trim(),
                email: wizardCliente.email.trim(),
                telefono: wizardCliente.telefono.trim()
            });
            const nuevoClienteId = resCliente.data.id;

            // Crear contacto si el usuario llenó datos
            if (wizardContactoTieneDatos()) {
                try {
                    await clientesAPI.createContacto(nuevoClienteId, {
                        nombre: wizardContacto.nombre.trim(),
                        cargo: wizardContacto.cargo.trim() || null,
                        email: wizardContacto.email.trim() || null,
                        telefono: wizardContacto.telefono.trim() || null
                    });
                } catch (errContacto) {
                    // No bloqueamos el flujo si el contacto falla — el cliente ya se creó.
                    toast.error('Cliente creado, pero no se pudo guardar el contacto');
                }
            }

            // Refrescar lista + auto-seleccionar el nuevo cliente en el padre
            invalidateClientes();
            const refresh = await clientesAPI.getAll();
            const lista = refresh.data || [];
            if (onClientesActualizados) onClientesActualizados(lista);

            const nuevo = lista.find(c => c.id === nuevoClienteId);
            if (nuevo && onClienteCreado) onClienteCreado(nuevo);

            toast.success('Cliente creado correctamente');
            setWizardOpen(false);
        } catch (err) {
            toast.error('Error al crear cliente: ' + (err.message || ''));
        } finally {
            setWizardSaving(false);
        }
    };

    return (
        <>
            {clienteSeleccionado && (
                <Tooltip content="Seleccionar contacto del cliente">
                    <button
                        type="button"
                        className="notif-action detalle"
                        onClick={abrirSelectorContactos}
                        aria-label="Seleccionar contacto"
                    >
                        <UserSearch size={14} />
                        <span>Contactos</span>
                    </button>
                </Tooltip>
            )}
            <Tooltip content="Crear nuevo cliente">
                <button
                    type="button"
                    className="notif-action version"
                    onClick={abrirWizardCliente}
                    aria-label="Crear nuevo cliente"
                >
                    <UserPlus size={14} />
                    <span>Nuevo cliente</span>
                </button>
            </Tooltip>
            {/* Wizard: crear nuevo cliente en 3 pasos (cliente → contacto opcional → confirmar) */}
            <Modal
                open={wizardOpen}
                onClose={cerrarWizard}
                title={
                    wizardStep === 1 ? 'Nuevo cliente · Datos generales'
                    : wizardStep === 2 ? 'Nuevo cliente · Contacto (opcional)'
                    : 'Nuevo cliente · Confirmar'
                }
                size="md"
                footer={
                    <div className="flex items-center justify-between w-full">
                        <span className="text-xs text-muted">Paso {wizardStep} de 3</span>
                        <div className="flex gap-2">
                            {wizardStep > 1 && (
                                <Button variant="ghost" leftIcon={<ArrowLeft size={14} />} onClick={retrocederWizard} disabled={wizardSaving}>
                                    Atrás
                                </Button>
                            )}
                            {wizardStep < 3 ? (
                                <Button variant="primary" rightIcon={<ArrowRight size={14} />} onClick={avanzarWizard}>
                                    Siguiente
                                </Button>
                            ) : (
                                <Button variant="primary" leftIcon={<Check size={14} />} onClick={confirmarCrearCliente} disabled={wizardSaving}>
                                    {wizardSaving ? 'Creando…' : 'Crear cliente'}
                                </Button>
                            )}
                        </div>
                    </div>
                }
            >
                {/* Paso 1: Datos del cliente */}
                {wizardStep === 1 && (
                    <div className="flex flex-col gap-3">
                        <ClienteTipoToggle
                            value={wizardCliente.tipo}
                            onChange={(tipo) => setWizardClienteCampo('tipo', tipo)}
                            name="cliente-tipo-wizard"
                        />
                        <Input
                            id="wiz-cli-nombre"
                            label="Nombre"
                            required
                            type="text"
                            value={wizardCliente.nombre}
                            onChange={(e) => setWizardClienteCampo('nombre', e.target.value)}
                            autoFocus
                        />
                        <Input
                            id="wiz-cli-empresa"
                            label="Empresa"
                            required={wizardCliente.tipo === TIPO_EMPRESA}
                            disabled={wizardCliente.tipo === TIPO_PERSONA}
                            hint={wizardCliente.tipo === TIPO_PERSONA
                                ? 'En persona natural se usa el nombre como razón social.'
                                : undefined}
                            type="text"
                            value={empresaEfectiva(wizardCliente)}
                            onChange={(e) => setWizardClienteCampo('empresa', e.target.value)}
                        />
                        <Input
                            id="wiz-cli-email"
                            label="Email"
                            type="email"
                            value={wizardCliente.email}
                            onChange={(e) => setWizardCliente(prev => ({ ...prev, email: e.target.value }))}
                        />
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="wiz-cli-telefono" className="text-xs font-medium text-muted">Teléfono</label>
                            <PhoneInput
                                id="wiz-cli-telefono"
                                value={wizardCliente.telefono}
                                onChange={(v) => setWizardCliente(prev => ({ ...prev, telefono: v || '' }))}
                            />
                        </div>
                    </div>
                )}

                {/* Paso 2: Contacto opcional */}
                {wizardStep === 2 && (
                    <div className="flex flex-col gap-3">
                        <p className="text-xs text-muted">
                            Agregar un contacto inicial. Puedes dejar todos los campos vacíos para omitir este paso.
                        </p>
                        <Input
                            id="wiz-ct-nombre"
                            label="Nombre del contacto"
                            type="text"
                            value={wizardContacto.nombre}
                            onChange={(e) => setWizardContacto(prev => ({ ...prev, nombre: e.target.value }))}
                            autoFocus
                        />
                        <Input
                            id="wiz-ct-cargo"
                            label="Cargo"
                            type="text"
                            value={wizardContacto.cargo}
                            onChange={(e) => setWizardContacto(prev => ({ ...prev, cargo: e.target.value }))}
                            placeholder="Ej: Gerente comercial"
                        />
                        <Input
                            id="wiz-ct-email"
                            label="Email"
                            type="email"
                            value={wizardContacto.email}
                            onChange={(e) => setWizardContacto(prev => ({ ...prev, email: e.target.value }))}
                        />
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="wiz-ct-telefono" className="text-xs font-medium text-muted">Teléfono</label>
                            <PhoneInput
                                id="wiz-ct-telefono"
                                value={wizardContacto.telefono}
                                onChange={(v) => setWizardContacto(prev => ({ ...prev, telefono: v || '' }))}
                            />
                        </div>
                    </div>
                )}

                {/* Paso 3: Confirmación */}
                {wizardStep === 3 && (
                    <div className="flex flex-col gap-4">
                        <div>
                            <h4 className="text-sm font-semibold text-text mb-2">Datos del cliente</h4>
                            <dl className="cliente-detalle-dl">
                                <div className="cliente-detalle-row">
                                    <dt className="cliente-detalle-label">Nombre</dt>
                                    <dd className="cliente-detalle-value">{wizardCliente.nombre}</dd>
                                </div>
                                {empresaEfectiva(wizardCliente) && (
                                    <div className="cliente-detalle-row">
                                        <dt className="cliente-detalle-label">Empresa</dt>
                                        <dd className="cliente-detalle-value">{empresaEfectiva(wizardCliente)}</dd>
                                    </div>
                                )}
                                {wizardCliente.email && (
                                    <div className="cliente-detalle-row">
                                        <dt className="cliente-detalle-label">Email</dt>
                                        <dd className="cliente-detalle-value">{wizardCliente.email}</dd>
                                    </div>
                                )}
                                {wizardCliente.telefono && (
                                    <div className="cliente-detalle-row">
                                        <dt className="cliente-detalle-label">Teléfono</dt>
                                        <dd className="cliente-detalle-value">{wizardCliente.telefono}</dd>
                                    </div>
                                )}
                            </dl>
                        </div>

                        {wizardContactoTieneDatos() ? (
                            <div>
                                <h4 className="text-sm font-semibold text-text mb-2">Contacto inicial</h4>
                                <dl className="cliente-detalle-dl">
                                    <div className="cliente-detalle-row">
                                        <dt className="cliente-detalle-label">Nombre</dt>
                                        <dd className="cliente-detalle-value">{wizardContacto.nombre}</dd>
                                    </div>
                                    {wizardContacto.cargo && (
                                        <div className="cliente-detalle-row">
                                            <dt className="cliente-detalle-label">Cargo</dt>
                                            <dd className="cliente-detalle-value">{wizardContacto.cargo}</dd>
                                        </div>
                                    )}
                                    {wizardContacto.email && (
                                        <div className="cliente-detalle-row">
                                            <dt className="cliente-detalle-label">Email</dt>
                                            <dd className="cliente-detalle-value">{wizardContacto.email}</dd>
                                        </div>
                                    )}
                                    {wizardContacto.telefono && (
                                        <div className="cliente-detalle-row">
                                            <dt className="cliente-detalle-label">Teléfono</dt>
                                            <dd className="cliente-detalle-value">{wizardContacto.telefono}</dd>
                                        </div>
                                    )}
                                </dl>
                            </div>
                        ) : (
                            <p className="text-xs text-muted italic">
                                Sin contacto inicial. Puedes agregarlos después desde la vista del cliente.
                            </p>
                        )}
                    </div>
                )}
            </Modal>

            {/* Modal selector de contactos del cliente. Al elegir uno, sus datos
                (nombre, email, telefono) reemplazan los campos del formulario.
                La empresa NO cambia — sigue siendo la del cliente principal. */}
            <Modal
                open={showContactosModal}
                onClose={() => setShowContactosModal(false)}
                title={`Contactos de ${clienteSeleccionado?.data?.nombre || 'cliente'}`}
                size="md"
                footer={
                    <Button variant="ghost" onClick={() => setShowContactosModal(false)}>
                        Cerrar
                    </Button>
                }
            >
                {loadingContactos ? (
                    <div className="loading">Cargando contactos…</div>
                ) : contactosCliente.length === 0 ? (
                    <div className="text-center text-muted py-6 text-sm">
                        Este cliente no tiene contactos registrados.
                        <div className="mt-3">
                            <Button
                                variant="primary"
                                size="sm"
                                leftIcon={<UserPlus size={14} />}
                                onClick={abrirCrearContacto}
                            >
                                Crear contacto
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-2">
                        {contactosCliente.map(c => (
                            <div
                                key={c.id}
                                className="flex items-center justify-between gap-3 p-3 rounded-lg border border-line bg-surface hover:bg-surface-2 transition"
                            >
                                <div className="flex flex-col min-w-0 flex-1">
                                    <span className="font-semibold text-text truncate">{c.nombre}</span>
                                    <span className="text-xs text-muted truncate">
                                        {c.cargo || 'Sin cargo'}
                                        {c.email && ` · ${c.email}`}
                                        {c.telefono && ` · ${c.telefono}`}
                                    </span>
                                </div>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    leftIcon={<UserCheck size={14} />}
                                    onClick={() => seleccionarContacto(c)}
                                >
                                    Seleccionar
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </Modal>

            {/* Modal para crear contacto rapido desde el selector (cuando el cliente no tiene aun).
                Al guardar, se autoselecciona y se rellena el form de cotizacion. */}
            <Modal
                open={showCrearContactoModal}
                onClose={savingNuevoContacto ? undefined : () => setShowCrearContactoModal(false)}
                title="Nuevo contacto"
                size="sm"
                footer={
                    <div className="flex justify-end gap-2 w-full">
                        <Button
                            variant="ghost"
                            onClick={() => setShowCrearContactoModal(false)}
                            disabled={savingNuevoContacto}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="primary"
                            leftIcon={<Check size={14} />}
                            onClick={guardarNuevoContacto}
                            disabled={savingNuevoContacto}
                        >
                            {savingNuevoContacto ? 'Creando…' : 'Crear y seleccionar'}
                        </Button>
                    </div>
                }
            >
                <div className="flex flex-col gap-3">
                    <Input
                        id="nc-nombre"
                        label="Nombre"
                        required
                        type="text"
                        value={nuevoContacto.nombre}
                        onChange={(e) => setNuevoContacto(prev => ({ ...prev, nombre: e.target.value }))}
                        autoFocus
                    />
                    <Input
                        id="nc-cargo"
                        label="Cargo"
                        type="text"
                        value={nuevoContacto.cargo}
                        onChange={(e) => setNuevoContacto(prev => ({ ...prev, cargo: e.target.value }))}
                        placeholder="Ej: Gerente comercial"
                    />
                    <Input
                        id="nc-email"
                        label="Email"
                        type="email"
                        value={nuevoContacto.email}
                        onChange={(e) => setNuevoContacto(prev => ({ ...prev, email: e.target.value }))}
                    />
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="nc-telefono" className="text-xs font-medium text-muted">Teléfono</label>
                        <PhoneInput
                            id="nc-telefono"
                            value={nuevoContacto.telefono}
                            onChange={(v) => setNuevoContacto(prev => ({ ...prev, telefono: v || '' }))}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default ClienteAcciones;
